import { Controller } from "@hotwired/stimulus"
import "@selectize/selectize"

export default class extends Controller {
  static get targets() {
    return [
      "allowedValues",
      "dataCount",
      "tag",
      "tagsList",
      "tagTemplate",
      "includeValues",
      "excludeValues",
      "logicalOperator",
      "selectedOperator",
    ]
  }

  static get values() {
    return { name: String }
  }

  // Setup the selectize element and add the already selected include and exclude values as tags.
  connect() {
    this._setUpSelectizeElement()

    // Combine include and exclude selected options into one array, and sort it alphabetically
    let combinedOptions = Array.from(this.includeValuesTarget.selectedOptions)
      .concat(Array.from(this.excludeValuesTarget.selectedOptions))
      .sort((a, b) => a.innerText.localeCompare(b.innerText))

    // Iterate over the sorted combined array to call _addTag
    combinedOptions.forEach(option => {
      // Determine if the option is from includeValuesTarget or excludeValuesTarget
      const isIncluded = Array.from(this.includeValuesTarget.selectedOptions).includes(option)
      this._addTag(option.value, option.innerText, isIncluded)
    })

    this._sortTags()
    this._refreshDataCount()
    this._refreshSelectize()
    this._toggleLogicalOperator()
  }

  // Clean up selectize
  disconnect() {
    this.selectizeElement?.destroy()
  }

  // Clear the filter
  clear() {
    Array.from(this.includeValuesTarget.selectedOptions).forEach(option => {
      let options = this._removeTag(option.value, true)
      this.selectizeElement.addOption({ value: option.value, text: options.text, optgroup: options.group })
    })

    Array.from(this.excludeValuesTarget.selectedOptions).forEach(option => {
      let options = this._removeTag(option.value, false)
      this.selectizeElement.addOption({ value: option.value, text: options.text, optgroup: options.group })
    })

    this._refreshDataCount()
    this._refreshSelectize()
    this._dispatchFilterEvent(true)
    this._toggleLogicalOperator()
  }

  addTag(event) {
    const tagValue = event.detail.eventParams.tagValue
    const tagLabel = event.detail.eventParams.tagLabel
    const group = event.detail.eventParams.group
    this._addTag(tagValue, tagLabel)
    this._addTagToSelect(tagValue, tagLabel, true, group)
    this._sortTags()
    this._refreshSelectize()
    this._dispatchFilterEvent()
    this._toggleLogicalOperator()
  }

  // Flip the include/exclude value of the given tag
  changeTag({ detail: { tagValue, included }}) {
    let options = this._removeTag(tagValue, !included)
    this._addTag(tagValue, options.text, included);
    this._addTagToSelect(tagValue, options.text, included, options.group)
    this._sortTags()
    this._dispatchFilterEvent()
    this.tagTargets.forEach(tag => {
      if (tag.dataset.value === tagValue) {
        tag.dataset.type = included ? "included" : "excluded"
      }
    })
    this._toggleLogicalOperator()
  }

  // Remove the tag from the list and add it back to the selectize element
  removeTag({ detail: { tagValue, included }}) {
    let options = this._removeTag(tagValue, included)

    this.selectizeElement.addOption({ value: tagValue, text: options.text, optgroup: options.group })
    this.selectizeElement.refreshOptions(false)

    // Check if the filter got cleared before dispatching the event
    const isLastTag = this.tagTargets.length === 0
    this._dispatchFilterEvent(isLastTag)
    this._toggleLogicalOperator()
  }

  changeOperator(event) {
    this.selectedOperatorTarget.value = event.target.parentElement.value
    this._dispatchFilterEvent()
  }

  // Setup selectize to add a tag when selecting a value and then remove it from the selectize element.
  _setUpSelectizeElement() {
    this.selectizeElement = $(this.allowedValuesTarget).selectize({
      persist: false,
      create: false,
      copyClassesToDropdown: false,
      sortField: "text",
      lockOptgroupOrder: true,
      onItemAdd: (value, $item) => {
        const label = $item[0].innerText
        const optgroup = this.selectizeElement.options[value].optgroup
        this.addTag({ detail: { eventParams: { tagValue: value, tagLabel: label, group: optgroup }}})
      },
      plugins: ["auto_position"],
    })[0].selectize
  }

  // This creates a tag using the HTML template and removes it from the selectize element.
  _addTag(value, label, included = true) {
    let tagHtml = this.tagTemplateTarget.innerHTML
    tagHtml = tagHtml.replace(/%LABEL%/g, label)
    tagHtml = tagHtml.replace(/%VALUE%/g, value)
    tagHtml = tagHtml.replace(/%INCLUDED%/g, included)
    tagHtml = tagHtml.replace(/%TYPE%/g, included ? "included" : "excluded")

    // Show tooltip for long label as they are truncated
    if (label.length > 22) {
      tagHtml = tagHtml.replace(/%TOOLTIP%/g, label)
    } else {
      tagHtml = tagHtml.replace(/%TOOLTIP%/g, "")
    }

    let tagNode = document.createElement('div')
    tagNode.innerHTML = tagHtml

    this.tagsListTarget.appendChild(tagNode.firstElementChild)

    this.selectizeElement.removeOption(value)
  }

  // Sort the tags in the tags list alphabetically by their label
  _sortTags() {
    const tagsArray = Array.from(this.tagsListTarget.children)
    tagsArray.sort((a, b) => a.getAttribute("data-label").localeCompare(b.getAttribute("data-label")))
    tagsArray.forEach(element => this.tagsListTarget.appendChild(element));
  }

  // Refresh the data-count attribute
  _refreshDataCount() {
    if (! this.hasDataCountTarget) {
      return
    }

    let selectedOptions = this.includeValuesTarget.selectedOptions.length + this.excludeValuesTarget.selectedOptions.length

    if (selectedOptions > 0) {
      this.dataCountTarget.dataset.count = selectedOptions
    } else {
      delete this.dataCountTarget.dataset.count
    }
  }

  // Refresh selectize, clear the selected values and close it
  _refreshSelectize() {
    this.selectizeElement.refreshOptions(false)
    this.selectizeElement.clear()
    this.selectizeElement.close()
  }

  // Remove the tag from the list and the related select element.
  _removeTag(tagValue, included) {
    let index = 0
    let selectElement = included ? this.includeValuesTarget : this.excludeValuesTarget
    let tagText
    let group

    for (let i = 0; i < selectElement.options.length; i++) {
      if (selectElement.options[i].value === tagValue) {
        tagText = selectElement.options[i].innerText
        group = selectElement.options[i].dataset.group
        index = i
        break
      }
    }

    this.tagTargets.forEach(tag => {
      if (tag.dataset.value === tagValue) {
        tag.remove()
      }
    })

    selectElement.options.remove(index)
    this._refreshDataCount()

    return { text: tagText, group: group }
  }

  // Add the tag value and label to the right select element (included or excluded values)
  _addTagToSelect(value, label, included, group) {
    let option = new Option(label, value, true, true)

    if (group) {
      option.dataset.group = group
    }

    let selectElement = included ? this.includeValuesTarget : this.excludeValuesTarget
    selectElement.options.add(option)
    this._refreshDataCount()
  }

  // Dispatch the filter event, this can be used with other elements to apply the filters.
  _dispatchFilterEvent(isClearing = false) {
    let filters = [
      { name: `${this.nameValue}_included`, values: this._selectedValues(this.includeValuesTarget) },
      { name: `${this.nameValue}_excluded`, values: this._selectedValues(this.excludeValuesTarget) },
    ]

    if (this.hasSelectedOperatorTarget) {
      filters.push({ name: `${this.nameValue}_operator`, values: isClearing ? [""] : [this.selectedOperatorTarget.value] })
    }

    this.dispatch("filters", { detail: { filters } })
  }

  // Utility method to get all values from a select element.
  _selectedValues(selectElement) {
    return Array.from(selectElement.selectedOptions).map(option => option.value)
  }

  _toggleLogicalOperator() {
    if (!this.hasLogicalOperatorTarget) {
      return
    }

    const hasAllTagsExcluded = Array.from(this.tagTargets).every(tag => tag.dataset.type === 'excluded')
    const toggleButtons = (disabled, activeButtonValue) => {
      this.logicalOperatorTarget.querySelectorAll('button').forEach(button => {
        button.disabled = disabled
        button.classList.toggle("is-active", button.value === activeButtonValue)
      })
    }

    if (this.tagTargets.length > 1) {
      this.logicalOperatorTarget.classList.remove("d-none")

      if (hasAllTagsExcluded) {
        this.selectedOperatorTarget.value = "and"
        this.logicalOperatorTarget.classList.add("disabled")
        toggleButtons(true, "and")
      } else {
        this.logicalOperatorTarget.classList.remove("disabled")
        toggleButtons(false, this.selectedOperatorTarget.value)
      }
    } else {
      this.logicalOperatorTarget.classList.add("d-none")
    }
  }
}
